import axios from "axios";
import { trackDesignCreated } from "../utils/analytics.utils";
import { DesignFromDB, DesignProduct, Response } from "../types";

const USER_API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const BASE_ROUTE = "/api/v1/designs";

class DesignService {
  static async getDesigns() {
    return axios.get(
      USER_API_BASE_URL + BASE_ROUTE + "/",
      this.getAuthHeader()
    );
  }

  static async getDesign(designId: string) {
    return axios.get<Response<DesignFromDB>>(
      USER_API_BASE_URL + BASE_ROUTE + `/${designId}`,
      this.getAuthHeader()
    );
  }

  static async getProjectIdFromDesignId(designId: string) {
    return axios.get(
      USER_API_BASE_URL + BASE_ROUTE + `/${designId}/projectId`,
      this.getAuthHeader()
    );
  }

  static async addDesign(
    data: {
      projectId: string;
      designId: string;
      name: string;
    },
    product: DesignProduct
  ) {
    const res = await axios.post(
      USER_API_BASE_URL + BASE_ROUTE + "/design",
      data,
      this.getAuthHeader()
    );

    const {
      data: {
        data: { id },
        status,
      },
    } = res;

    if (status === "success" && id) {
      trackDesignCreated(product.name);
    }

    return res;
  }

  static async moveDesign(data: { designId: string; newProjectId: string }) {
    return axios.post(
      USER_API_BASE_URL + BASE_ROUTE + "/movedesign",
      data,
      this.getAuthHeader()
    );
  }

  static async deleteDesign(id: string) {
    return axios.delete(
      USER_API_BASE_URL + BASE_ROUTE + "/design/" + id,
      this.getAuthHeader()
    );
  }

  static async renameDesign(data: { designId: string; name: string }) {
    return axios.put(
      `${USER_API_BASE_URL}${BASE_ROUTE}/design/name`,
      data,
      this.getAuthHeader()
    );
  }

  static async propagateDesignStateUpdate(data: { designId: string }) {
    return axios.put(
      `${USER_API_BASE_URL}${BASE_ROUTE}/design/designStateUpdate`,
      data,
      this.getAuthHeader()
    );
  }

  static async postCollectSize(data: {
    details: {
      firstName: string;
      lastName: string;
      email: string;
    };
    selection: {
      id: string;
      size: string;
      quantity: number;
    }[];
  }) {
    return axios.post(`${USER_API_BASE_URL}${BASE_ROUTE}/collectSize`, data);
  }

  static async changeDescription(data: {
    description: string;
    designId: string;
  }) {
    return axios.put(
      `${USER_API_BASE_URL}${BASE_ROUTE}/design/description`,
      data,
      this.getAuthHeader()
    );
  }

  static async updateCollectable(data: {
    collectable: boolean;
    designId: string;
  }) {
    return axios.put(
      USER_API_BASE_URL + BASE_ROUTE + "/design/collectable",
      data,
      this.getAuthHeader()
    );
  }

  static getAuthHeader() {
    return {
      headers: {},
      withCredentials: true,
    };
  }

  static async newsLetterSubscribe(email: string, lang: string) {
    const data = { email: `${email}`, lang: `${lang} ` };
    try {
      await axios.post(
        `${USER_API_BASE_URL}${BASE_ROUTE}/newsletter`,
        data,
        this.getAuthHeader()
      );
      return true;
    } catch (error) {
      console.error(error);
    }
    return false;
  }

  static async getDesignAsAdmin(designId: string, userIdToValidate: string) {
    const data = {
      designId: designId,
      userId: userIdToValidate,
    };
    return axios.post(
      `${USER_API_BASE_URL}${BASE_ROUTE}/design/search`,
      data,
      this.getAuthHeader()
    );
  }

  static async getRecentDesigns() {
    return axios.get(
      `${USER_API_BASE_URL}${BASE_ROUTE}/recent`,
      this.getAuthHeader()
    );
  }

  static async setFavoriteDesign(designId: string, isFavorite: boolean) {
    return axios.put(
      `${USER_API_BASE_URL}${BASE_ROUTE}/design/favorite`,
      {
        designId,
        isFavorite,
      },
      this.getAuthHeader()
    );
  }

  static async lockDesign(
    designId: string,
    locked: boolean,
    lockedVersion?: string
  ) {
    return axios.put(
      `${USER_API_BASE_URL}${BASE_ROUTE}/design/locked`,
      {
        designId,
        locked,
        lockedVersion,
      },
      this.getAuthHeader()
    );
  }

  static async addDesignToProject(data: {
    designId: string;
    newProjectId: string;
  }) {
    return axios.post(
      `${USER_API_BASE_URL}${BASE_ROUTE}/design/template`,
      data,
      this.getAuthHeader()
    );
  }
}

export default DesignService;
